import React, { useState, useEffect, useCallback } from 'react';
import StoreCard from './StoreCard';
import DateSelectionBox from './DateSelectionBox';
import PaymentStub from './PaymentStub';
import CountrySearchBar from './CountrySearchBar';
import WaitlistPopup from './WaitlistPopup';
import routePreloader from '../services/routePreloader';
import { trackViewRoute, trackInitiateCheckout } from '../utils/facebookPixel';
import './StorePage.css';
// import CurrencyConverter from './CurrencyConverter'; // TODO: Implement currency conversion

function StorePage() {
  const [routes, setRoutes] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flippedCards, setFlippedCards] = useState(new Set());
  const [flipDelayedCards, setFlipDelayedCards] = useState(new Set());
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showInauguralPopup, setShowInauguralPopup] = useState(false);
  const [showWaitlistPopup, setShowWaitlistPopup] = useState(false);
  const [waitlistRoute, setWaitlistRoute] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Hide default app layout elements
  useEffect(() => {
    // Hide any parent headers/footers
    const hideElements = () => {
      const elementsToHide = document.querySelectorAll('header, nav, .header, .navbar, footer, .footer');
      elementsToHide.forEach(el => {
        if (!el.closest('.store-page')) {
          el.style.display = 'none';
        }
      });
      
      // Make body full screen
      document.body.style.margin = '0';
      document.body.style.padding = '0';
      document.body.style.overflow = 'auto';
    };

    hideElements();
    
    // Cleanup on unmount
    return () => {
      const elementsToShow = document.querySelectorAll('header, nav, .header, .navbar, footer, .footer');
      elementsToShow.forEach(el => {
        if (!el.closest('.store-page')) {
          el.style.display = '';
        }
      });
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.overflow = '';
    };
  }, []);

  const fetchRoutes = useCallback(async () => {
    setLoading(true);
    try {
      // Check if routes are already preloaded
      let visibleRoutes = routePreloader.getRoutes();

      if (!visibleRoutes) {
        // If not preloaded, fetch them now
        const dataFetchPromise = routePreloader.preloadRoutes();

        // Create promise for minimum delay
        const delayPromise = new Promise(resolve => setTimeout(resolve, 666));

        // Wait for both data fetch and minimum delay
        [visibleRoutes] = await Promise.all([dataFetchPromise, delayPromise]);
      } else {
        // Data is preloaded, just apply a short delay for smooth transition
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      setRoutes(visibleRoutes);
      setFilteredRoutes(visibleRoutes);

      // Extract unique countries from all routes
      const countriesSet = new Map();
      visibleRoutes.forEach(route => {
        if (route.selectedCountries && Array.isArray(route.selectedCountries)) {
          route.selectedCountries.forEach(country => {
            if (country.code && country.name) {
              countriesSet.set(country.code, {
                code: country.code,
                name: country.name
              });
            }
          });
        }
      });

      const uniqueCountries = Array.from(countriesSet.values())
        .sort((a, b) => a.name.localeCompare(b.name));
      setAvailableCountries(uniqueCountries);

    } catch (err) {
      console.error('Error fetching routes:', err);
      setError('Failed to load routes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  // Handle search - now supports filtering by countries, cities, and route content
  const handleCountrySearch = useCallback((searchData) => {
    const { query } = searchData;

    // Store the query for display purposes
    setSelectedCountry({ name: query, isQuery: true });

    // Reset any flipped cards when searching
    setFlippedCards(new Set());
    // Delay the content switch to match the flip timing
    setTimeout(() => {
      setFlipDelayedCards(new Set());
    }, 160);

    if (!query || query.trim() === '') {
      setFilteredRoutes(routes);
      setCurrentIndex(0);
      return;
    }

    const lowercasedQuery = query.toLowerCase();
    const searchTerms = lowercasedQuery.split(/\s+/).filter(term => term.length > 0);

    // Score each route based on matches (like mobile explore cards)
    const scoredRoutes = routes.map(route => {
      let score = 0;
      let matchedTerms = 0;

      searchTerms.forEach(term => {
        // Route name matches (highest priority - 10 points)
        if (route.name && route.name.toLowerCase().includes(term)) {
          score += 10;
          matchedTerms++;
        }

        // Country matches (high priority - 8 points)
        if (route.selectedCountries && Array.isArray(route.selectedCountries)) {
          route.selectedCountries.forEach(country => {
            if (country.name && country.name.toLowerCase().includes(term)) {
              score += 8;
              matchedTerms++;
            }
            if (country.code && country.code.toLowerCase().includes(term)) {
              score += 8;
              matchedTerms++;
            }
          });
        }

        // Tagline matches (medium priority - 5 points)
        if (route.storeTagline && route.storeTagline.toLowerCase().includes(term)) {
          score += 5;
          matchedTerms++;
        }

        // Bullet points matches (medium priority - 3 points)
        if (route.storeBulletPoints && Array.isArray(route.storeBulletPoints)) {
          route.storeBulletPoints.forEach(point => {
            const mainText = typeof point === 'string' ? point : point?.mainText || '';
            const subText = typeof point === 'string' ? '' : point?.subText || '';

            if (mainText.toLowerCase().includes(term)) {
              score += 3;
              matchedTerms++;
            }
            if (subText.toLowerCase().includes(term)) {
              score += 3;
              matchedTerms++;
            }
          });
        }
      });

      return {
        ...route,
        searchScore: score,
        matchedTerms: matchedTerms
      };
    });

    // Filter to only routes that matched at least one term
    const filtered = scoredRoutes
      .filter(route => route.matchedTerms > 0)
      .sort((a, b) => b.searchScore - a.searchScore); // Sort by score descending

    setFilteredRoutes(filtered);
    setCurrentIndex(0); // Reset to first card
  }, [routes]);

  // Handle search clear
  const handleSearchClear = useCallback(() => {
    setSelectedCountry(null);
    setFilteredRoutes(routes);
    setCurrentIndex(0);
  }, [routes]);

  // Carousel navigation
  const navigateCarousel = useCallback((direction) => {
    setCurrentIndex(prevIndex => {
      if (direction === 'next') {
        return prevIndex === filteredRoutes.length - 1 ? 0 : prevIndex + 1;
      } else {
        return prevIndex === 0 ? filteredRoutes.length - 1 : prevIndex - 1;
      }
    });

    // Auto-flip any flipped cards back to front when navigating
    setFlippedCards(new Set());
    // Delay the content switch to match the flip timing
    setTimeout(() => {
      setFlipDelayedCards(new Set());
    }, 160);
  }, [filteredRoutes.length]);

  // Navigate to specific index (when clicking side cards)
  const navigateToIndex = useCallback((index) => {
    setCurrentIndex(index);

    // Auto-flip any flipped cards back to front when navigating
    setFlippedCards(new Set());
    // Delay the content switch to match the flip timing
    setTimeout(() => {
      setFlipDelayedCards(new Set());
    }, 160);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        navigateCarousel('prev');
      } else if (e.key === 'ArrowRight') {
        navigateCarousel('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigateCarousel]);

  const handleCardFlip = (routeId) => {
    const isCurrentlyFlipped = flippedCards.has(routeId);
    
    // Start the CSS animation immediately
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(routeId)) {
        newSet.delete(routeId);
      } else {
        newSet.add(routeId);
      }
      return newSet;
    });
    
    // Switch content at 160ms into 900ms animation (per user preference)
    setTimeout(() => {
      setFlipDelayedCards(prev => {
        const newSet = new Set(prev);
        if (isCurrentlyFlipped) {
          newSet.delete(routeId);
        } else {
          newSet.add(routeId);
        }
        return newSet;
      });
    }, 160);
  };

  const handlePriceClick = (route) => {
    console.log('🔵 [STOREPAGE DEBUG] handlePriceClick() TRIGGERED', { route });

    // Check if it's available or waitlist - only check status since storePrice isn't saved
    const isPriceAvailable = route.storeStatus === 'beta' || route.storeStatus === 'available';
    console.log('🔵 [STOREPAGE DEBUG] isPriceAvailable:', isPriceAvailable);

    if (isPriceAvailable) {
      console.log('🔵 [STOREPAGE DEBUG] Route available - calling tracking functions...');

      // Track ViewContent event (user is viewing route details)
      // Use route.storePrice if available, otherwise default to 599 (industry standard base price)
      const estimatedPrice = route.storePrice || 599;
      console.log('🔵 [STOREPAGE DEBUG] About to call trackViewRoute() with price:', estimatedPrice);
      trackViewRoute(route, 'USD', estimatedPrice);
      console.log('🔵 [STOREPAGE DEBUG] trackViewRoute() call completed');

      // Track InitiateCheckout (user is starting the booking process)
      console.log('🔵 [STOREPAGE DEBUG] About to call trackInitiateCheckout() with price:', estimatedPrice);
      trackInitiateCheckout(route, 'USD', estimatedPrice);
      console.log('🔵 [STOREPAGE DEBUG] trackInitiateCheckout() call completed');

      console.log('🔵 [STOREPAGE DEBUG] Opening date selection popup...');
      setSelectedRoute(route);
      setShowPopup(true);
    } else {
      console.log('🔵 [STOREPAGE DEBUG] Route not available - showing waitlist popup');
      // Show waitlist popup
      setWaitlistRoute(route);
      setShowWaitlistPopup(true);
    }
  };

  const handleInauguralClick = () => {
    setShowInauguralPopup(true);
  };

  if (loading) {
    return (
      <div className="store-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="store-page">
        <div className="error-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="store-page"
      style={{
        width: '100vw',
        minHeight: '100vh',
        backgroundImage: `linear-gradient(rgba(7, 34, 49, 0.9), rgba(7, 34, 49, 0.9)), url('/images/servicesection.webp')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'scroll',
        overflowY: 'visible',
      }}
      onClick={(e) => {
        // If clicking outside of a centered card, flip it back to front
        if (!e.target.closest('.carousel-card-center')) {
          // Start the flip animation immediately
          setFlippedCards(new Set());

          // Delay the content switch to match the flip timing
          setTimeout(() => {
            setFlipDelayedCards(new Set());
          }, 160);
        }
      }}
    >
      {/* Custom font style */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;800&display=swap');
          @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;500;600;700&display=swap');
          
          @font-face {
            font-family: 'PassFont';
            src: url('/fonts/PassFont.ttf') format('truetype');
            font-weight: 400;
            font-style: normal;
            font-display: swap;
          }
          
          @font-face {
            font-family: 'MainFontLight';
            src: url('/fonts/MainFontLight.ttf') format('truetype');
            font-weight: 300;
            font-style: normal;
            font-display: swap;
          }
          
          .store-page {
            font-family: 'PassFont', -apple-system, BlinkMacSystemFont, sans-serif !important;
          }
          
          .store-page button,
          .store-page input,
          .store-page select {
            font-family: inherit !important;
          }
        `}
      </style>
      
      {/* Logo Header */}
      <div className="logo-header">
        <a href="/">
          <img src="/images/logodownsized.webp" alt="BackPass Logo" className="logo" />
        </a>
      </div>

      {/* Country Search Bar */}
      <div className="search-bar-container">
        <CountrySearchBar
          countries={availableCountries}
          onSearch={handleCountrySearch}
          onClear={handleSearchClear}
        />
      </div>

      {/* Routes Carousel */}
      <section className="routes-section">
        {filteredRoutes.length > 0 ? (
          <div className="carousel-container">
            {/* Carousel Cards */}
            <div className="carousel-track">
              {/* Navigation Arrows moved inside track for better positioning */}
              {filteredRoutes.length > 1 && (
                <>
                  <button
                    className="carousel-arrow carousel-arrow-left"
                    onClick={() => navigateCarousel('prev')}
                    aria-label="Previous route"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button
                    className="carousel-arrow carousel-arrow-right"
                    onClick={() => navigateCarousel('next')}
                    aria-label="Next route"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </>
              )}
              {filteredRoutes.map((route, index) => {
                const isFlipped = flippedCards.has(route.id);
                const showBackContent = flipDelayedCards.has(route.id);
                const isPriceAvailable = route.storeStatus === 'beta' || route.storeStatus === 'available';

                // Calculate position relative to current index
                let position = index - currentIndex;

                // Handle wrap-around
                if (position > filteredRoutes.length / 2) {
                  position -= filteredRoutes.length;
                } else if (position < -filteredRoutes.length / 2) {
                  position += filteredRoutes.length;
                }

                // Determine card class
                let cardClass = 'carousel-card';
                if (position === 0) {
                  cardClass += ' carousel-card-center';
                } else if (position === -1) {
                  cardClass += ' carousel-card-left';
                } else if (position === 1) {
                  cardClass += ' carousel-card-right';
                } else if (position === -2) {
                  cardClass += ' carousel-card-far-left';
                } else if (position === 2) {
                  cardClass += ' carousel-card-far-right';
                } else {
                  cardClass += ' carousel-card-hidden';
                }

                const isCenter = position === 0;
                const isSide = position === -1 || position === 1 || position === -2 || position === 2;

                return (
                  <div
                    key={route.id}
                    className={cardClass}
                    onClick={() => {
                      if (isSide) {
                        navigateToIndex(index);
                      }
                    }}
                    style={{
                      cursor: isSide ? 'pointer' : 'default'
                    }}
                  >
                    <StoreCard
                      route={route}
                      isFlipped={isFlipped}
                      showBackContent={showBackContent}
                      isPriceAvailable={isPriceAvailable}
                      onFlip={isCenter ? () => handleCardFlip(route.id) : () => {}}
                      onPriceClick={isCenter ? handlePriceClick : () => {}}
                      onInauguralClick={handleInauguralClick}
                    />
                  </div>
                );
              })}
            </div>

            {/* Dots Indicator */}
            {filteredRoutes.length > 1 && (
              <div className="carousel-dots">
                {filteredRoutes.map((_, index) => (
                  <button
                    key={index}
                    className={`carousel-dot ${index === currentIndex ? 'carousel-dot-active' : ''}`}
                    onClick={() => navigateToIndex(index)}
                    aria-label={`Go to route ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="no-routes-message">
            <p>No routes found{selectedCountry ? ` for ${selectedCountry.name}` : ''}.</p>
            {selectedCountry && (
              <button onClick={handleSearchClear} className="clear-filter-button">
                Show All Routes
              </button>
            )}
          </div>
        )}
      </section>

      {/* Popup Modal with DateSelectionBox */}
      {showPopup && selectedRoute && (
        <div className="popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <DateSelectionBox
              selectedRoute={selectedRoute}
              onBookNow={(data) => {
                setPaymentData(data);
                setShowPaymentPopup(true);
              }}
              onClose={() => setShowPopup(false)}
            />
          </div>
        </div>
      )}

      {/* Payment Popup Modal */}
      {showPaymentPopup && paymentData && (
        <div className="popup-overlay" onClick={() => setShowPaymentPopup(false)}>
          <div className="payment-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={() => setShowPaymentPopup(false)}>×</button>
            <PaymentStub 
              paymentData={paymentData}
              onClose={() => setShowPaymentPopup(false)}
            />
          </div>
        </div>
      )}

      {/* Inaugural Info Popup */}
      {showInauguralPopup && (
        <div className="popup-overlay" onClick={() => setShowInauguralPopup(false)}>
          <div className="inaugural-popup-content" onClick={(e) => e.stopPropagation()}>
            <button className="popup-close" onClick={() => setShowInauguralPopup(false)}>×</button>
            <h3>The Inaugural Pass</h3>
            <p>As an inaugural member, you will have personalized communication from BackPass every step of the way. Think of it as a hyper-discounted travel agent. In return we'll use your feedback to make BackPass better :)</p>
          </div>
        </div>
      )}

      {/* Waitlist Signup Popup */}
      {showWaitlistPopup && waitlistRoute && (
        <WaitlistPopup
          route={waitlistRoute}
          onClose={() => {
            setShowWaitlistPopup(false);
            setWaitlistRoute(null);
          }}
        />
      )}
    </div>
  );
}

export default StorePage;